import { forwardRef } from "react";
import {
  Typography as MuiTypography,
  type TypographyProps as MuiTypographyProps,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { brand } from "../../theme/brand";

import type { Theme } from "@mui/material/styles";

/** The locked set of Centro type variants. Each maps 1:1 to a MUI variant. */
export type TypographyVariant =
  | "display-large"
  | "display-medium"
  | "display-small"
  | "header-large"
  | "header-medium"
  | "header-small"
  | "subtitle1"
  | "subtitle2"
  | "body-large"
  | "body-medium"
  | "caption"
  | "overline";

/**
 * Token-bound text colors. `danger` is the one status color text may carry on
 * its own; warnings and success states belong to Alert. `brandPrimary` and
 * `brandSecondary` are design's `fgColor.primary` and `fgColor.secondary`, for
 * standalone text that carries the brand without being a link. `inherit` takes
 * the color from the surrounding element, such as a button label or a banner.
 */
export type TypographyColor =
  | "textPrimary"
  | "textSecondary"
  | "textDisabled"
  | "danger"
  | "brandPrimary"
  | "brandSecondary"
  | "inherit";

/**
 * The elements a variant may render as. `component` is the semantic choice
 * (heading level, inline or block) and `variant` the visual one, so this list
 * holds text elements only; a link or a button is its own base component.
 */
export type TypographyElement =
  | "span"
  | "p"
  | "div"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "li"
  | "label";

export interface TypographyProps extends Omit<
  MuiTypographyProps,
  // Locked: type scale and family are driven by `variant` only, never per-instance.
  | "variant"
  | "color"
  | "component"
  // `variantMapping` would let a variant render any element, around `component`'s list.
  | "variantMapping"
  | "fontFamily"
  | "fontSize"
  | "fontWeight"
  | "lineHeight"
  | "letterSpacing"
> {
  /** Type variant. Default: "body-large". */
  variant?: TypographyVariant;
  /** Text color, bound to a theme token. Default: "textPrimary". */
  color?: TypographyColor;
  /** The element to render. Default: the variant's own (`h1` for display-large, `p` for body, …). */
  component?: TypographyElement;
  /** Root test id. Default: "ds-typography". */
  "data-testid"?: string;
}

const variantMap: Record<TypographyVariant, MuiTypographyProps["variant"]> = {
  "display-large": "h1",
  "display-medium": "h2",
  "display-small": "h3",
  "header-large": "h4",
  "header-medium": "h5",
  "header-small": "h6",
  subtitle1: "subtitle1",
  subtitle2: "subtitle2",
  "body-large": "body1",
  "body-medium": "body2",
  caption: "caption",
  overline: "overline",
};

// The brand entries read `brand()`, which falls back to the brand constants
// when no theme provides a brand palette.
const colorMap: Record<TypographyColor, string | ((theme: Theme) => string)> = {
  textPrimary: "text.primary",
  textSecondary: "text.secondary",
  textDisabled: "text.disabled",
  danger: "error.main",
  brandPrimary: brand("primary"),
  brandSecondary: brand("supporting"),
  inherit: "inherit",
};

/** Constrains MUI Typography to the locked Centro type scale and text-color tokens. */
export const Typography = forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant = "body-large",
      color = "textPrimary",
      "data-testid": dataTestId = "ds-typography",
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const mapped = colorMap[color];
    return (
      <MuiTypography
        ref={ref}
        data-testid={dataTestId}
        variant={variantMap[variant]}
        color={typeof mapped === "function" ? mapped(theme) : mapped}
        {...rest}
      />
    );
  },
);

Typography.displayName = "Typography";
